/**
 * リアルタイムチャットAPI（利用者側）
 *
 * AppSync Events WebSocket経由のリアルタイムメッセージング。
 * 管理者版（tenant-elive）をベースに、利用者が必要とする機能のみ提供。
 * - メッセージ履歴取得（admin=false で hidden を除外）
 * - メッセージ送信用の型定義
 * - モデレーション操作（hide/unhide/deleteAll）は含まない
 */

import type { ChatMessage } from '../types/chat';
import { ensureFrontendSettings, getCachedFrontendSettings } from './frontendSettings';

// =============================================================================
// 型定義
// =============================================================================

/** AppSync Events 接続設定 */
export interface AppSyncEventsConfig {
  realtimeEndpoint: string;
  httpEndpoint: string;
  apiKey: string;
  historyApiEndpoint: string;
}

/** メッセージ送信データ */
export interface SendMessageData {
  userId: string;
  userName: string;
  message: string;
  userType?: 'student' | 'admin';
}

/** WebSocket 受信メッセージのペイロード */
export interface MessageEventPayload {
  type: 'message' | 'moderation' | 'error';
  messageId?: string;
  timestamp?: number;
  tenantId?: string;
  lectureId?: string;
  userId?: string;
  userName?: string;
  message?: string;
  status?: 'visible' | 'hidden';
  hasNgWords?: boolean;
  isNgUser?: boolean;
  detectedNgWords?: string[];
  userType?: 'student' | 'admin';
  action?: 'hide' | 'unhide' | 'delete_all';
  hiddenBy?: string;
  unhiddenBy?: string;
  deletedCount?: number;
  errorType?: string;
  deadline?: string;
}

/** メッセージ履歴APIレスポンス */
export interface MessageHistoryResponse {
  messages: Array<{
    messageId: string;
    timestamp: number;
    tenantId: string;
    lectureId: string;
    userId: string;
    userName: string;
    message: string;
    status: 'visible' | 'hidden';
    hasNgWords?: boolean;
    isNgUser?: boolean;
    detectedNgWords?: string[];
    userType?: string;
    createdAt: string;
  }>;
  count: number;
  limit: number;
  nextToken?: string | null;
  hasMore?: boolean;
}

// =============================================================================
// 設定取得
// =============================================================================

/**
 * チャット設定を取得（frontend-settings APIのキャッシュを参照）
 *
 * 呼び出し前に ensureFrontendSettings() で設定を取得しておくこと。
 */
export function getChatConfig(): AppSyncEventsConfig {
  const settings = getCachedFrontendSettings();
  const realtimeEndpoint = settings?.chatRealtimeEndpoint || '';
  const httpEndpoint = settings?.chatHttpEndpoint || '';
  const apiKey = settings?.chatApiKey || '';
  const historyApiEndpoint = settings?.chatHistoryApiEndpoint || '';

  if (!realtimeEndpoint || !httpEndpoint || !apiKey || !historyApiEndpoint) {
    console.warn('[Chat] チャット設定が不完全です。管理画面のAppSync設定を確認してください。');
  }

  return { realtimeEndpoint, httpEndpoint, apiKey, historyApiEndpoint };
}

// =============================================================================
// API関数
// =============================================================================

/**
 * AppSync Events HTTP エンドポイント経由でメッセージを送信する。
 * WebSocket の publish はサポートされないため、HTTP POST で送信する。
 */
export async function publishMessage(
  channel: string,
  data: SendMessageData,
): Promise<void> {
  await ensureFrontendSettings();
  const config = getChatConfig();
  const url = `https://${config.httpEndpoint}/event`;

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      // Lambda Authorizer 認証（SEC-L4対応で AppSync組み込みAPIキー（x-api-key）から
      // Authorization トークンに変更）
      Authorization: config.apiKey,
    },
    body: JSON.stringify({
      channel,
      events: [JSON.stringify(data)],
    }),
  });

  if (!response.ok) {
    const errorText = await response.text();
    console.error('[Chat] メッセージ送信エラー:', response.status, errorText);
    throw new Error(`メッセージの送信に失敗しました: ${response.status}`);
  }
}

/**
 * メッセージ履歴を取得する。
 * 利用者側では admin=false で呼び出し、hidden メッセージはサーバー側で除外される。
 */
export async function getMessageHistory(
  tenantId: string,
  lectureId: string,
  limit: number = 100,
  nextToken?: string | null,
): Promise<{ messages: ChatMessage[]; hasMore: boolean; nextToken: string | null }> {
  await ensureFrontendSettings();
  const config = getChatConfig();
  const url = new URL(`${config.historyApiEndpoint}/messages/history`);
  url.searchParams.append('tenantId', tenantId);
  url.searchParams.append('lectureId', lectureId);
  url.searchParams.append('limit', limit.toString());
  if (nextToken) {
    url.searchParams.append('nextToken', nextToken);
  }

  try {
    // チャットアクセスキー認証（バックエンドの Lambda Authorizer が Secrets Manager と照合）
    const response = await fetch(url.toString(), {
      headers: {
        'x-api-key': config.apiKey,
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('[Chat] メッセージ履歴取得エラー:', response.status, errorText);
      throw new Error(`メッセージ履歴の取得に失敗しました: ${response.status}`);
    }

    const data: MessageHistoryResponse = await response.json();

    const messages: ChatMessage[] = data.messages.map((msg) => {
      const mappedMsg = {
        // id は React の key・再同期時の重複判定に使用するため、サーバー採番の
        // messageId を優先する。timestamp のみだと同一ミリ秒の複数メッセージで
        // 衝突する（STU-11対応）。messageId がない旧データは timestamp + userId で一意化する
        id: msg.messageId != null ? String(msg.messageId) : `${msg.timestamp}-${msg.userId ?? ''}`,
        userId: msg.userId,
        userName: msg.userName,
        userType: (msg.userType === 'admin' ? 'instructor' : 'student') as ChatMessage['userType'],
        message: msg.message,
        timestamp: new Date(msg.timestamp).toISOString(),
        isHidden: msg.status === 'hidden',
        messageId: msg.messageId,
        status: msg.status,
        ...(msg.hasNgWords != null && { hasNgWords: msg.hasNgWords }),
        ...(msg.isNgUser != null && { isNgUser: msg.isNgUser }),
      };
      
      if (msg.hasNgWords || msg.isNgUser) {
        console.log('[getMessageHistory] NGメッセージ検出:', {
          messageId: mappedMsg.id,
          userId: mappedMsg.userId,
          hasNgWords: mappedMsg.hasNgWords,
          isNgUser: mappedMsg.isNgUser,
          originalHasNgWords: msg.hasNgWords,
          originalIsNgUser: msg.isNgUser,
        });
      }
      
      return mappedMsg;
    });

    return {
      messages,
      hasMore: data.hasMore || false,
      nextToken: data.nextToken || null,
    };
  } catch (error) {
    console.error('[Chat] メッセージ履歴取得失敗:', error);
    throw error;
  }
}
